import { Link, useParams } from "@tanstack/react-router";
import { X, Settings, LogOut, Search, HelpCircle } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { useCore } from "../lib/core";
import { useCommandRegistry } from "@praesto/core";
import { useBodyScrollLock } from "@praesto/ui-kit";
import { useHelp } from "../help/HelpHost";

/**
 * Slide-in right drawer for additional actions: search, settings, theme,
 * logout. Reached via bottom-nav "Altro" button.
 */
export function MobileMoreDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const core = useCore();
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };
  const { setOpen } = useCommandRegistry();
  const onOpenCommandPalette = () => setOpen(true);
  const help = useHelp();

  // ui-kit's hook, not a hand-rolled effect: the previous version forced
  // `document.body.style.overflow = ""` on cleanup unconditionally, so closing
  // this drawer — which AppLayout does on every route change — unlocked the
  // body underneath any other overlay that was still open, the guide panel
  // included. The hook restores the previous value instead.
  useBodyScrollLock(open);

  return (
    <div className={`fixed inset-0 z-40 ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
      <div
        className={`absolute inset-0 bg-overlay transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <aside
        className={`absolute top-0 bottom-0 right-0 w-[85%] max-w-[320px] bg-elevated shadow-lg flex flex-col pt-safe pb-safe transform transition-transform duration-200 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="px-4 py-3 border-b border-border-default flex items-center justify-between">
          <h2 className="text-eyebrow">{core.i18n.t("shell.more.title")}</h2>
          <button
            onClick={onClose}
            className="tap-target flex items-center justify-center -mr-2 rounded-sm hover:bg-hover"
            aria-label={core.i18n.t("shell.action.close")}
          >
            <X size={18} />
          </button>
        </div>
        <div className="py-2 px-2 space-y-1 overflow-y-auto">
          <button
            onClick={() => {
              onOpenCommandPalette();
              onClose();
            }}
            className="tap-target w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm hover:bg-hover"
          >
            <Search size={18} className="shrink-0" /> {core.i18n.t("shell.more.search")}
          </button>
          <Link
            to={`/${tenantSlug}/settings` as never}
            onClick={onClose}
            className="tap-target flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm hover:bg-hover"
          >
            <Settings size={18} className="shrink-0" /> {core.i18n.t("shell.more.settings")}
          </Link>
          <button
            onClick={() => {
              // Close this drawer first: stacking the guide panel on top of it
              // leaves two overlays fighting for the same 375px.
              onClose();
              help.open();
            }}
            className="tap-target w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm hover:bg-hover"
          >
            <HelpCircle size={18} className="shrink-0" /> {core.i18n.t("help.title")}
          </button>
          <div className="tap-target flex items-center justify-between px-3 py-2.5">
            <span className="text-sm">{core.i18n.t("shell.more.theme")}</span>
            <ThemeToggle />
          </div>
          <button
            onClick={() => void core.auth.signOut()}
            className="tap-target w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm hover:bg-hover text-danger"
          >
            <LogOut size={18} className="shrink-0" /> {core.i18n.t("shell.more.signout")}
          </button>
        </div>
      </aside>
    </div>
  );
}
